import { isNonEmptyArray, notEmpty } from '@shared/utils/array';
import type { ReactNode } from 'react';
import { useContext } from 'react';
import type { EquipmentCategory } from '@/feature/equipment/api-types';
import detailsContext from '../pages/detailsContext';

type MatchMode = 'every' | 'some' | 'none';

type FilterableEquipment = Equipment | EquipmentEntity;

export interface EquipmentContentFilterProps extends FilterOptions {
  equipment: (FilterableEquipment | undefined)[] | undefined;
  children: ReactNode;
  empty?: ReactNode;
}

interface FilterOptions {
  match?: MatchMode;
  categories?: (EquipmentCategory | string)[];
  selection?: number;
  connected?: boolean;
}

interface FilterOptionsStrict {
  match: MatchMode;
  categories: string[];
  selection?: number;
  connected?: boolean;
}

export const EquipmentContentFilter = ({
  equipment = [],
  children,
  empty = null,
  ...filter
}: EquipmentContentFilterProps) => {
  const matches = useEquipmentContentFilter(filter);

  return matches(equipment.filter(notEmpty)) ? <>{children}</> : <>{empty}</>;
};

interface SelectedEquipmentContentFilterProps extends Omit<
  EquipmentContentFilterProps,
  'equipment'
> {
  loading?: ReactNode;
}

export const SelectedEquipmentContentFilter = ({
  loading = null,
  children,
  ...other
}: SelectedEquipmentContentFilterProps) => {
  const { selectedEquipment, isLoading } = useContext(detailsContext);

  if (isLoading) {
    return <>{loading}</>;
  }

  return (
    <EquipmentContentFilter {...other} equipment={selectedEquipment}>
      {children}
    </EquipmentContentFilter>
  );
};

type MatcherFn = (
  eqList: FilterableEquipment[],
  options: FilterOptionsStrict
) => boolean;

const matchesSelection: MatcherFn = (
  eqList: FilterableEquipment[],
  { selection }
) => {
  if (typeof selection !== 'number') {
    return true;
  }

  return eqList.length <= selection;
};

const matchesCategories: MatcherFn = (
  eqList: FilterableEquipment[],
  { categories, match }
) => {
  if (!isNonEmptyArray(categories)) {
    return true;
  }
  if (eqList.length === 0) {
    return false;
  }
  const includesCategory = (eq: FilterableEquipment) =>
    categories.includes(eq.category);

  switch (match) {
    case 'some':
      return eqList.some(includesCategory);
    case 'none':
      return !eqList.some(includesCategory);
    default:
    case 'every':
      return eqList.every(includesCategory);
  }
};

const matchesConnected: MatcherFn = (
  eqList: FilterableEquipment[],
  { connected, match }
) => {
  if (typeof connected !== 'boolean') {
    return true;
  }
  if (eqList.length === 0) {
    return false;
  }
  const isConnected = (eq: FilterableEquipment) => {
    if ('connected' in eq) {
      return eq.connected === connected;
    }
    console.warn(
      'Filtering on connected equipment is not supported for `EquipmentEntity`. Use `Equipment` instead.'
    );
    return false;
  };

  return match === 'some'
    ? eqList.some(isConnected)
    : eqList.every(isConnected);
};

export const useEquipmentContentFilter = ({
  match = 'every',
  categories = [],
  selection,
  connected,
}: FilterOptions) => {
  return (equipment?: FilterableEquipment | FilterableEquipment[]) => {
    if (equipment === undefined) {
      return false;
    }
    const eqList = Array.isArray(equipment) ? equipment : [equipment];
    const options: FilterOptionsStrict = {
      match,
      categories,
      selection,
      connected,
    };

    return (
      matchesSelection(eqList, options) &&
      matchesCategories(eqList, options) &&
      matchesConnected(eqList, options)
    );
  };
};

export const useIsHammer = () => {
  const isMatch = useEquipmentContentFilter({
    categories: ['HAMMER_HYDRAULIC'],
  });
  return (equipment: FilterableEquipment | undefined) => isMatch(equipment);
};
